import { Stack, useNavigationContainerRef } from "expo-router";
import { GluestackUIProvider } from "ui/gluestack/gluestack-ui-provider";
import { persistor, store } from "app/services/store";
import { Provider, useSelector } from "react-redux";
import * as Sentry from "@sentry/react-native";
import { PortalProvider } from "ui/modules/ThrelsPortal/PortalProvider";
import { SplashScreenView } from "app/components/molecules/SplashScreenView";
// Import your global CSS file
import "../global.css";
import { useEffect } from "react";
import { selectShouldShowOverlay } from "app/store/slices/overlay";
import {
  SENTRY_DSN,
  SENTRY_TRACE_SAMPLING_RATE,
  SENTRY_ENVIRONMENT,
  API_URL,
} from "app/config";
import { FeedbackForm } from "ui/molecules/feedback";
import { useNetworkStateListener } from "app/features/network/hooks/useNetworkStateListener";
import { PersistGate } from "redux-persist/integration/react";
import { ActivityIndicator, View } from "react-native";
import { SessionProvider } from "app/contexts/SessionContext";

const navigationIntegration = Sentry.reactNavigationIntegration({
  enableTimeToInitialDisplay: true,
});

Sentry.init({
  dsn: SENTRY_DSN,
  environment: SENTRY_ENVIRONMENT,
  tracePropagationTargets: [API_URL, /^\/api/],
  integrations: [
    Sentry.reactNativeTracingIntegration(),
    navigationIntegration,
    Sentry.mobileReplayIntegration(),
  ],
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  enableNativeFramesTracking: true,
  enableAutoSessionTracking: true,
  tracesSampleRate: SENTRY_TRACE_SAMPLING_RATE,
  debug: SENTRY_ENVIRONMENT === "development",
  beforeSend(event) {
    // Filter out noisy span events in development
    if (SENTRY_ENVIRONMENT === "development" && event.type === "transaction") {
      return null;
    }
    return event;
  },
});

function StoreChildren() {
  useNetworkStateListener();

  const showOverlay = useSelector(selectShouldShowOverlay);
  return (
    <>
      <Stack
        screenOptions={{
          headerShown: false,
          title: "iCare",
        }}
      />
      <FeedbackForm />

      {showOverlay ? <SplashScreenView /> : null}
    </>
  );
}

function RootLayout() {
  // Capture the NavigationContainer ref and register it with the integration.
  const ref = useNavigationContainerRef();

  useEffect(() => {
    if (ref?.current) {
      navigationIntegration.registerNavigationContainer(ref);
    }
  }, [ref]);

  useEffect(() => {
    document.title = "iCare"; // Set the page title
  }, []);

  return (
    <PortalProvider>
      <Provider store={store}>
        <PersistGate
          loading={
            <View className="flex-1 items-center justify-center">
              <ActivityIndicator size="large" />
            </View>
          }
          persistor={persistor}
          onBeforeLift={() => {
            // Optional: Add any logic to run before state rehydration
            console.log("Rehydrating state from IndexedDB...");
          }}
        >
          <GluestackUIProvider mode="light">
            <SessionProvider>
              <StoreChildren />
            </SessionProvider>
          </GluestackUIProvider>
        </PersistGate>
      </Provider>
    </PortalProvider>
  );
}

export default Sentry.wrap(RootLayout);
